/**
 * API layer — barrel export + singleton GameApi instance.
 *
 * Usage:
 *   import { gameApi } from '../api';
 *   gameApi.placeBet({ amount: 10, slotId: 1 });
 *   const unsub = gameApi.on('tick', (p) => console.log(p.multiplier));
 */

import type { GameApi } from './GameApi';
import { MockGameApi } from './mock/MockGameApi';
import { RealGameApi } from './ws/RealGameApi';
import type { Game } from '../game/Game';

export type { GameApi, GameEventMap, Unsubscribe } from './GameApi';
export type { MockGameApi } from './mock/MockGameApi';

// Re-export all types for convenience
export type * from './types';

const WS_URL = (import.meta as { env?: Record<string, string> }).env?.VITE_WS_ENDPOINT || '';

/** Is the app running in real (WebSocket) mode? */
export const isRealMode: boolean = WS_URL.length > 0;

/** Singleton GameApi — mock or real based on VITE_WS_ENDPOINT */
export const gameApi: GameApi = isRealMode
  ? new RealGameApi(WS_URL)
  : new MockGameApi();

/**
 * Get the underlying Game instance (mock mode only).
 * Returns null in real mode.
 * Used by legacy code that needs direct Game access (audio, rocket, etc.).
 */
export function getGameInstance(): Game | null {
  if (!isRealMode && gameApi instanceof MockGameApi) {
    return (gameApi as MockGameApi).getGame();
  }
  return null;
}
